import { DocsProps, DocumentList } from '@/components/investors/DocumentList';
import { MediumSmallTitle, SmallTitle } from '@/components/investors/shared';
import { InvestorsPageTemplate } from '@/pageTemplate/InvestorsPageTemplage';
import { Flex, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { COLORS } from '@lemonade-hq/tokens';
import Image from 'next/image';
import { rgba } from 'polished';
import React from 'react';
import styled from 'styled-components';
// images
import adina from '@/static/assets/investor/governance/Adina-compress.webp';
import daniel from '@/static/assets/investor/governance/Daniel-compress.webp';
import deb from '@/static/assets/investor/governance/Deb-compress.webp';
import geoff from '@/static/assets/investor/governance/Geoff.webp';
import john from '@/static/assets/investor/governance/John-compress.webp';
import maria from '@/static/assets/investor/governance/Maria-compress.webp';
import maya from '@/static/assets/investor/governance/Maya-compress.webp';
import michael from '@/static/assets/investor/governance/Michael-compress.webp';
import michal from '@/static/assets/investor/governance/Michal-compress.webp';
import prashant from '@/static/assets/investor/governance/Prashant.webp';
import samer from '@/static/assets/investor/governance/Samer-compress.webp';
import shai from '@/static/assets/investor/governance/Shai-compress.webp';
import tim from '@/static/assets/investor/governance/Tim.webp';

import { SeoProps } from '@/components/shared/SEO';
import { useRouter } from 'next/router';

const BoardMemberIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} fill="none">
    <path
      fill="#4A4A4A"
      fillRule="evenodd"
      d="M5.75 5.75a2.25 2.25 0 1 1 4.5 0 2.25 2.25 0 0 1-4.5 0Zm4.803 2.747a3.75 3.75 0 1 0-5.105 0c-1.863.78-3.198 2.442-3.198 4.753a.75.75 0 0 0 1.5 0C3.75 11.004 5.544 9.5 8 9.5s4.25 1.504 4.25 3.75a.75.75 0 0 0 1.5 0c0-2.31-1.335-3.974-3.197-4.753Z"
      clipRule="evenodd"
    />
  </svg>
);

const BoardChairIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} fill="none">
    <path
      fill="#4A4A4A"
      fillRule="evenodd"
      d="M10.553 8.497a3.75 3.75 0 1 0-5.105 0c-1.863.78-3.198 2.442-3.198 4.753 0 .414.336.75.75.75h10a.75.75 0 0 0 .75-.75c0-2.31-1.335-3.974-3.197-4.753Z"
      clipRule="evenodd"
    />
  </svg>
);

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const StyledRow = styled.tr`
  border-bottom: 1px solid ${themedColor('glass')};
`;

const StyledHeaderCell = styled.th`
  padding: 16px 8px;
  text-align: ${props => props.align || 'left'};
`;

const StyledDataCell = styled.td`
  padding: 16px 8px;
  text-align: ${props => props.align || 'left'};
  white-space: nowrap;
`;

const LegendContainer = styled(Flex)`
  display: flex;
  gap: 20px;
  margin-top: 10px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Grid = styled.div`
  --grid-size: calc(50% - 8px);
  display: grid;
  grid-template-columns: repeat(auto-fit, var(--grid-size));
  row-gap: 36px;
  column-gap: 12px;

  ${MEDIA_QUERIES.tabletAndDesktop`
    --grid-size: calc(25% - 15px);
    grid-template-columns: repeat(auto-fit, var(--grid-size));
    row-gap: 52px;
    column-gap: 20px;
  `}
`;

const GridCard = styled(Flex)`
  flex-direction: column;
  gap: 16px;
  cursor: pointer;

  &:hover {
    img {
      box-shadow: 0px 10px 24px -2px ${rgba(COLORS.black, 0.12)};
    }
  }
`;

const MemberTitle = styled.span`
  font-size: 1.6rem;
  text-align: left;
  line-height: 145%;
  text-wrap: pretty;
`;

const ReponsiveImage = styled(Image)`
  width: 100%;
  height: auto;
  object-fit: cover;
  border-radius: 16px;
  transition: all ease 0.2s;
  box-shadow: 0 0 0 0 ${rgba(COLORS.black, 0)};
`;

const GridComponent: React.FC<{
  data: { image: string | StaticImageData; name: string; title: string; content: string }[];
}> = ({ data }) => {
  const router = useRouter();
  return (
    <Grid>
      {data.map(({ name, image, title }) => {
        return (
          <GridCard
            role="button"
            key={name}
            onClick={() => router.push(`/investor/governance/${name.toLowerCase().replaceAll(' ', '-')}`)}
          >
            <ReponsiveImage src={image} alt={name} priority />
            <Flex flexDirection="column" width="100%">
              <SmallTitle>{name}</SmallTitle>
              <MemberTitle>{title}</MemberTitle>
            </Flex>
          </GridCard>
        );
      })}
    </Grid>
  );
};

export const EXECUTIVE_MANAGEMENT_DATA = [
  {
    name: 'Daniel Schreiber',
    image: daniel,
    title: 'CEO & Cofounder',
    content:
      "Daniel Schreiber has served as our Co-Founder, Chief Executive Officer, and Chairman of our board of directors since our founding in June 2015. Prior to co-founding Lemonade in 2015, Mr. Schreiber served as President and a member of the board of directors of Powermat Technologies Ltd., a wireless charging solutions and technology company, from 2011 to 2015. From 2003 to 2011, he served as Senior Vice President of Marketing and Vice President of Marketing and Business Development at SanDisk and M-Systems (which was acquired by SanDisk in 2006), respectively. In 1997, Mr. Schreiber co-founded and acted as the Chief Executive Officer of Alchemedia Inc., an internet security software company acquired by Finjan Software in 2002. Prior to that, Mr. Schreiber practiced corporate commercial law at Herzog, Fox & Neeman, and was a member of the Israeli Bar Association. He holds a Bachelor of Laws with First Class Honors from King's College London.",
  },
  {
    name: 'Shai Wininger',
    image: shai,
    title: 'President & Cofounder',
    content:
      'Shai Wininger has served in various roles, including as our Co-Founder, President, Secretary, Treasurer, Chief Operating Officer, Chief Technology Officer, and a member of our board of directors, since our founding in June 2015. Prior to co-founding Lemonade in 2015, Mr. Wininger founded Fiverr Ltd. in 2009, and as the Chief Technology Officer, managed the engineering, design, and product departments. Prior to 2010, Mr. Wininger served in senior management capacities for companies including: from 2005 to 2010, Mobideo Aerospace, an industrial grade analytics and control platform; from 2003 to 2005, Handsmart Software, a mobile licensing platform for content driven, mobile apps; and from 1999 to 2003, Trimus Inc., a virtual reality web browser. Mr. Wininger also served as a resident faculty member of Computer Graphics at The Neri Bloomfield Academy of Design and Education from 2002 to 2007 in Haifa, Israel.',
  },
  {
    name: 'Tim Bixby',
    image: tim,
    title: 'Chief Financial Officer',
    content:
      'Tim Bixby has served as our Chief Financial Officer since June 2017. Since 2021, Tim has served as Audit Chair and Board Director of Rent the Runway. Prior to joining Lemonade, Mr. Bixby served as Chief Financial Officer of Shutterstock, Inc., a digital content licensing marketplace, from 2011 to 2015. From 1999 to 2011 he served as the Chief Financial Officer, President, and a member of the board of directors of LivePerson, Inc., a provider of cloud mobile and online business messaging solutions. He holds a Bachelor of Arts in Mathematics from Dartmouth College and a Master of Business Administration from Harvard Business School.',
  },
  {
    name: 'Adina Eckstein',
    image: adina,
    title: 'Chief Operations Officer',
    content:
      'Adina Eckstein has served as our Chief Operating Officer (COO) since July 2021, and previously was the company’s Vice President of Operations. Prior to joining Lemonade, Ms. Eckstein served as COO of Digital Channels at HSBC, where she helped bring one of the world’s largest financial institutions into the digital age. Previously, Ms. Eckstein was Vice President of Programme and Portfolio at BBC Worldwide, where she led the development and operations of all consumer digital technology. From 2008-2014, she served in VP Product Delivery and VP Sales roles, and was a founding team member at Tvinci, a technology start-up company. She holds a Masters of Business Administration from Tel Aviv University.',
  },
  {
    name: 'John Peters',
    image: john,
    title: 'Chief Insurance Officer',
    content:
      "John Peters has served as our Chief Insurance Officer since September 2016. Prior to joining Lemonade, during the period from 2011 to 2016, he served as the Executive Vice President of Commercial Insurance Operations and the Chief Underwriting and Product Officer, Regional Companies Group for Liberty Mutual Insurance in Boston. Mr. Peters also spent ten years at McKinsey & Company's global property-casualty insurance practice, serving in various roles including partner. He holds a Bachelor of Arts in Mathematics and German from Bowdoin College and is a former fellow of the Casualty Actuarial Society.",
  },
  {
    name: 'Maya Prosor',
    image: maya,
    title: 'Chief Business Officer',
    content:
      'Maya Prosor is a founding team member of Lemonade and has served as our Chief Business Officer since July 2022. Prior to becoming our Chief Business Officer, Ms. Prosor served in different roles at Lemonade, including Homeowners company lead from July 2020 to July 2022, and VP business development from August 2015 to July 2020. Prior to joining Lemonade in 2015, Ms. Prosor was the AVP of market development for the joint venture of Duracell and Powermat Technologies Ltd., a wireless charging solutions and technology company, from 2010 to 2015. She holds a Bachelor of Arts in Business Administration and Entrepreneurship from Reichman University and is a graduate of the Zell Entrepreneurship Program.',
  },
  {
    name: 'Michal Langer',
    image: michal,
    title: 'Chief Product Officer',
    content:
      "Michal Langer has served as our Chief Product Officer since July 2022. Prior to becoming Chief Product Officer, Ms. Langer served in various leadership roles at Lemonade, including VP of Product Strategy and Operations, General Manager of Lemonade Car, and General Manager of Lemonade Pet, where she built and grew two new businesses in the company's portfolio. Before joining Lemonade, she spent six years at eBay in senior product roles, including Director of Product reporting to the Chief Product Officer. This included operational oversight of new products, eBay's global brand, eBay's design, and the company’s charitable giving group. Earlier in her career, she held product management, analytics, and QA management positions at various companies and startups. She holds a Bachelor of Science in Physics with honors from The Hebrew University of Jerusalem.",
  },
];

const ExecutiveManagement = () => {
  return (
    <Flex flexDirection="column" gap="20px">
      <MediumSmallTitle>Executive Management</MediumSmallTitle>
      <GridComponent data={EXECUTIVE_MANAGEMENT_DATA} />
    </Flex>
  );
};

export const BOARD_OF_DIRECTORS_DATA = [
  EXECUTIVE_MANAGEMENT_DATA[0], // daniel
  EXECUTIVE_MANAGEMENT_DATA[1], // shai
  {
    name: 'Michael Eisenberg',
    image: michael,
    title: 'Equal Partner, Aleph',
    content:
      'Michael Eisenberg has served as a member of our board of directors since July 2015. Mr. Eisenberg is a Partner at Aleph, an early stage venture capital fund that invests in Israeli entrepreneurs, which he joined in July 2013. In addition to his role on our board of directors, Mr. Eisenberg serves as a member of the board of several private companies. He holds a Bachelor of Arts in Political Science from Yeshiva University.',
  },
  {
    name: 'Deb Schwartz',
    image: deb,
    title: 'Former CFO, H1',
    content:
      'Deb Schwartz has served as a member of our board of directors since November 2023. Ms. Schwartz is a seasoned financial leader skilled at enabling companies to innovate, grow and scale. She was until recently the Chief Financial Officer of H1, a leading healthcare data technology company whose mission is to connect the world to the right doctors. She previously served as CFO at Cameo, the celebrity video shoutout pioneer, and at Bustle Digital Group, the digital media provider. Ms. Schwartz spent more than a decade as an equity analyst with Goldman Sachs and Credit Suisse, and holds an MBA from Harvard University, and a BA/BS from the University of Pennsylvania.',
  },
  {
    name: 'Dr. Samer Haj-Yehia',
    image: samer,
    title: 'Executive Chairman & Cofounder, Nevo Investment and ExBorder Partners',
    content:
      'Dr. Samer Haj-Yehia has served as a member of our board of directors since November 2023. Dr. Haj-Yehia brings extensive executive and board experience at various conglomerates across multiple industries in Israel and the US. Until October 2023, he was the Group Executive Chairman of Bank Leumi, Israel’s largest and oldest bank. Under his leadership since 2019, Leumi became the largest and most efficient bank in Israel, grew its income and profitability, and underwent technological transformation and innovation. While in the US, Dr. Haj-Yehia practiced investment management, trading, and fintech innovation at leading financial institutions, including at Fidelity. He also served as a member of public and government committees, teaches finance and fintech at Reichman University, and is a guest speaker at international conferences. Dr. Haj-Yehia holds a Ph.D. in economics from MIT, and an MBA (summa cum laude), LLB, MA (magna cum laude) in economics, and BA (magna cum laude) in accounting, all from Hebrew University. He is a CFA charterholder.',
  },
  {
    name: 'Maria Angelidou-Smith',
    image: maria,
    title: 'Chief Product Officer, Reddit, Inc.',
    content:
      'Maria Angelidou-Smith has served as a member of our board of directors since October 2024. Ms. Angelidou-Smith was until recently the Chief Product and Technology Officer of Personio, Europe’s leading HR software provider empowering small and mid-sized organizations. Prior to Personio, she served as a Vice President of Product and General Manager at Meta, leading and scaling the monetization of the Facebook App as well as billion+ user products such as Facebook Groups, Events and Profile. She previously held leadership roles with several companies, including Intuit and The Boston Consulting Group, focused primarily on product development, growth strategies, and marketing. She holds a BBA in Economics, Operations and Marketing from Athens University of Economics and Business, and an MBA from University of Michigan - Stephen M. Ross School of Business, where she was a Fullbright Scholar. ',
  },
  {
    name: 'Prashant Ratanchandani',
    image: prashant,
    title: 'VP of Engineering for AI Products, Meta',
    content:
      'Prashant Ratanchandani is the VP of Engineering for AI Products at Meta, supporting the engineering teams responsible for building Meta’s Generative AI products. He has held several leadership roles at Meta, building and scaling technical organizations responsible for core experiences across Meta products. Prior to Meta, he held leadership roles at Microsoft, with a focus on the fundamentals of the Windows operating system. He holds a Bachelor of Technology in Computer Science from the Indian Institute of Technology, Kharagpur where he received the B.C Roy Gold Medal, and also holds a Master of Science degree from the University of Illinois at Urbana Champaign.',
  },
  {
    name: 'Geoff Seeley',
    image: geoff,
    title: 'Chief Marketing Officer, Paypal Holdings, Inc.',
    content:
      'Geoff Seeley is the Chief Marketing Officer of PayPal Holdings, Inc., a role he has held since February 2024, where he leads global marketing for PayPal and Venmo. Previously, Mr. Seeley served as Global Chief Marketing and Communications Officer at Afterpay, where he helped scale both consumer and merchant adoption —culminating in the $29 billion sale to Block, Inc. (formerly Square). Previously, he led global marketing at Airbnb, driving brand equity resulting in guest and host growth. Mr. Seeley previously held senior roles at Unilever and Pearson over a 30+ year career spanning brand strategy and performance marketing, large-scale rebrands, and modernization of marketing technology and analytics. He is a frequent speaker at industry forums. Mr. Seeley holds a B.Sc. from University College London.',
  },
];

const BoardOfDirectors = () => {
  return (
    <Flex flexDirection="column" gap="20px" mt={40} px={[20, 20, 0]}>
      <MediumSmallTitle>Board of Directors</MediumSmallTitle>
      <GridComponent data={BOARD_OF_DIRECTORS_DATA} />
    </Flex>
  );
};

const CommitteeComposition = () => {
  return (
    <Flex flexDirection="column" gap="20px" mt={40}>
      <Flex px={[20, 20, 0]}>
        <MediumSmallTitle>Committee Composition</MediumSmallTitle>
      </Flex>
      <Flex style={{ overflowX: 'auto' }} px={[20, 20, 0]}>
        <Flex
          minWidth={['800px', '100%', '100%']}
          style={{
            overflow: 'hidden',
          }}
        >
          <Table>
            <thead>
              <StyledRow>
                <StyledHeaderCell></StyledHeaderCell>
                <StyledHeaderCell align="center">Audit Committee</StyledHeaderCell>
                <StyledHeaderCell align="center">Compensation Committee</StyledHeaderCell>
                <StyledHeaderCell align="center">Nominating and Corporate Governance Committee</StyledHeaderCell>
              </StyledRow>
            </thead>
            <tbody>
              <StyledRow>
                <StyledDataCell>Michael Eisenberg</StyledDataCell>
                <StyledDataCell align="center"></StyledDataCell>
                <StyledDataCell align="center">
                  <BoardChairIcon />
                </StyledDataCell>
                <StyledDataCell align="center">
                  <BoardMemberIcon />
                </StyledDataCell>
              </StyledRow>
              <StyledRow>
                <StyledDataCell>Deb Schwartz</StyledDataCell>
                <StyledDataCell align="center">
                  <BoardMemberIcon />
                </StyledDataCell>
                <StyledDataCell align="center"></StyledDataCell>
                <StyledDataCell align="center">
                  <BoardChairIcon />
                </StyledDataCell>
              </StyledRow>
              <StyledRow>
                <StyledDataCell>Dr. Samer Haj-Yehia</StyledDataCell>
                <StyledDataCell align="center">
                  <BoardChairIcon />
                </StyledDataCell>
                <StyledDataCell align="center"></StyledDataCell>
                <StyledDataCell align="center"></StyledDataCell>
              </StyledRow>
              <StyledRow>
                <StyledDataCell>Maria Angelidou-Smith</StyledDataCell>
                <StyledDataCell align="center"></StyledDataCell>
                <StyledDataCell align="center">
                  <BoardMemberIcon />
                </StyledDataCell>
                <StyledDataCell align="center">
                  <BoardMemberIcon />
                </StyledDataCell>
              </StyledRow>
              <StyledRow>
                <StyledDataCell>Prashant Ratanchandani</StyledDataCell>
                <StyledDataCell align="center">
                  <BoardMemberIcon />
                </StyledDataCell>
                <StyledDataCell align="center"></StyledDataCell>
                <StyledDataCell align="center"></StyledDataCell>
              </StyledRow>
              <StyledRow>
                <StyledDataCell>Geoff Seeley</StyledDataCell>
                <StyledDataCell align="center"></StyledDataCell>
                <StyledDataCell align="center">
                  <BoardMemberIcon />
                </StyledDataCell>
                <StyledDataCell align="center"></StyledDataCell>
              </StyledRow>
            </tbody>
          </Table>
        </Flex>
      </Flex>
      <LegendContainer px={[20, 20, 0]}>
        <LegendItem>
          <BoardChairIcon /> Committee Chair
        </LegendItem>
        <LegendItem>
          <BoardMemberIcon /> Committee Member
        </LegendItem>
      </LegendContainer>
    </Flex>
  );
};

const DOCS = [
  {
    file: `${process.env.NEXT_PUBLIC_INVESTOR_ASSETS_URL}/governance/Audit-Committee-Charter-Oct-25.pdf`,
    name: 'Audit Committee Charter',
  },
  {
    file: `${process.env.NEXT_PUBLIC_INVESTOR_ASSETS_URL}/governance/Compensation-Committee-Charter-Final.pdf`,
    name: 'Compensation Committee Charter',
  },
  {
    file: `${process.env.NEXT_PUBLIC_INVESTOR_ASSETS_URL}/governance/Nom-Gov-Committee-Charter-Final.pdf`,
    name: 'Nominating and Corporate Governance Committee Charter',
  },
  {
    file: `${process.env.NEXT_PUBLIC_INVESTOR_ASSETS_URL}/governance/Corporate-Governance-Guidelines-Final.pdf`,
    name: 'Corporate Governance Guidelines',
  },
  {
    file: `${process.env.NEXT_PUBLIC_INVESTOR_ASSETS_URL}/governance/Code-of-Business-Conduct-and-Ethics-Dec-9-2020.pdf`,
    name: 'Code of Business Conduct and Ethics',
  },
  {
    file: `${process.env.NEXT_PUBLIC_INVESTOR_ASSETS_URL}/governance/Vendor-Code-of-Conduct.pdf`,
    name: 'Vendor Code of Conduct',
  },
] as DocsProps['docs'];

const Documents = () => {
  return (
    <Flex mt={40} flexDirection="column" gap="30px" px={[20, 20, 0]}>
      <MediumSmallTitle>Documents</MediumSmallTitle>
      <DocumentList docs={DOCS} />
    </Flex>
  );
};

const MAX_WIDTH = 800;

const seo = {
  title: 'Lemonade Inc. - Governance',
  description:
    'Review Lemonade’s corporate governance practices, board of directors, and committee charters to understand our leadership and ethical oversight.',
} as SeoProps;

export default function GovernancePage() {
  return (
    <InvestorsPageTemplate seo={seo}>
      <Flex flexDirection="column" gap="20px" width="100%" maxWidth={MAX_WIDTH}>
        <Flex flexDirection="column" gap="20px" px={[20, 20, 0]}>
          <ExecutiveManagement />
        </Flex>
        <BoardOfDirectors />
        <CommitteeComposition />
        <Documents />
      </Flex>
    </InvestorsPageTemplate>
  );
}
